import * as THREE from 'three';
import type { EraDef } from '../content/types';
import { Rng } from '../core/rng';
import { wrapDelta } from '../sim/world';
import { toonMaterial } from './geometry';
import { instancedOutline } from './outline';
import type { ModelLibrary } from './models';
import { environmentProfile, type EnvironmentProfile, type LandmarkKind } from './environmentProfiles';

const _m = new THREE.Matrix4();
const _p = new THREE.Vector3();
const _q = new THREE.Quaternion();
const _s = new THREE.Vector3();
const _up = new THREE.Vector3(0, 1, 0);

/**
 * Procedural environment for an era: a camera-following tiled ground plane
 * and scattered primitive props. Everything is generated from the EraDef; the
 * ground texture is drawn on a canvas by `groundStyle`. Props use a cosmetic
 * RNG seeded from the era id (never the sim RNG).
 */
export class Environment {
  readonly group = new THREE.Group();
  private propMeshes: THREE.InstancedMesh[] = [];
  private propAnims: { mesh: THREE.InstancedMesh; anim: string; base: Float32Array }[] = [];
  private propBase = new Map<THREE.InstancedMesh, Float32Array>();
  private propOutlines: THREE.InstancedMesh[] = [];
  private propScaleY = new Map<THREE.InstancedMesh, number>();
  private ground: THREE.Mesh | null = null;
  private ring: THREE.Mesh | null = null;
  private landmarks: { group: THREE.Group; x: number; z: number }[] = [];
  private ambient: THREE.Points | null = null;
  private ambientBase: Float32Array | null = null;
  private ambientProfile: EnvironmentProfile | null = null;
  private detailOn = true;
  private stageIndex = -1;
  private time = 0;
  private half = 60;
  private tile = 6;
  /** 0..1 prop presence: 0 = dissolved (time jump), 1 = fully assembled. Applied as a scale on every prop. */
  presence = 1;

  constructor(readonly scene: THREE.Scene, private models: ModelLibrary) {
    scene.add(this.group);
  }

  build(era: EraDef): void {
    this.clear();
    const R = era.environment.arenaRadius;
    this.half = R;
    const rng = new Rng('env:' + era.id);

    // ground: a large tiled plane that follows the camera (snapped to the texture period so it never swims)
    const G = 140; // covers the view at any camera distance
    const tex = new THREE.CanvasTexture(drawGround(era));
    tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
    tex.repeat.set(G / this.tile, G / this.tile);
    tex.colorSpace = THREE.SRGBColorSpace;
    const groundMat = new THREE.MeshStandardMaterial({ map: tex, color: 0xffffff, roughness: 0.95, metalness: 0 });
    this.ground = new THREE.Mesh(new THREE.PlaneGeometry(G, G), groundMat);
    this.ground.rotation.x = -Math.PI / 2;
    this.ground.receiveShadow = true;
    this.group.add(this.ground);

    this.buildLandmarks(era, rng);
    this.buildAmbient(era, rng);
    this.setStage(0);

    // props
    for (const prop of era.environment.props) {
      // gltf drop-in: geometryFor() returns the merged model geometry when
      // prop.visual.gltf loaded, else the primitive (render/models.ts)
      const usingModel = !!prop.visual.gltf && this.models.has(prop.visual.gltf);
      const geo = this.models.geometryFor(prop.visual);
      // a model's own baked vertex colors replace the flat visual.color tint
      const mat = usingModel
        ? this.models.materialFor(prop.visual)
        : toonMaterial(prop.visual.color, prop.visual.emissive ?? 0, prop.visual.emissiveIntensity ?? 0);
      const mesh = new THREE.InstancedMesh(geo, mat, prop.count);
      mesh.castShadow = true;
      mesh.receiveShadow = true;
      const base = new Float32Array(prop.count * 4); // x, y, z, scale
      for (let i = 0; i < prop.count; i++) {
        // uniform over the torus, keeping the spawn point clear
        let x = rng.range(-R, R);
        let z = rng.range(-R, R);
        if (Math.hypot(x, z) < 6) {
          x += 8;
          z += 8;
        }
        const s = rng.range(prop.scale[0], prop.scale[1]);
        const y = (prop.y ?? 0) + s * 0.5 * (prop.visual.scaleY ?? 1);
        base[i * 4] = x;
        base[i * 4 + 1] = y;
        base[i * 4 + 2] = z;
        base[i * 4 + 3] = s;
        _p.set(x, y, z);
        _q.setFromAxisAngle(_up, rng.range(0, Math.PI * 2));
        _s.set(s, s * (prop.visual.scaleY ?? 1), s);
        _m.compose(_p, _q, _s);
        mesh.setMatrixAt(i, _m);
      }
      mesh.instanceMatrix.needsUpdate = true;
      mesh.frustumCulled = false;
      this.group.add(mesh);
      const outline = instancedOutline(mesh, 0.05);
      outline.count = prop.count;
      outline.visible = this.outlinesOn;
      this.group.add(outline);
      this.propOutlines.push(outline);
      this.propMeshes.push(mesh);
      this.propBase.set(mesh, base);
      this.propScaleY.set(mesh, prop.visual.scaleY ?? 1);
      if (prop.visual.anim && prop.visual.anim !== 'none') this.propAnims.push({ mesh, anim: prop.visual.anim, base });
    }
  }

  setOutlines(on: boolean): void {
    this.outlinesOn = on;
    this.detailOn = on;
    for (const o of this.propOutlines) o.visible = on;
    if (this.ambient) this.ambient.visible = on;
  }
  private outlinesOn = true;

  /** Stage-specific set dressing: sparse approach, denser objective field, full boss arena. */
  setStage(index: number): void {
    const next = Math.max(0, Math.min(2, index | 0));
    if (next === this.stageIndex) return;
    this.stageIndex = next;
    this.landmarks.forEach((landmark, i) => {
      landmark.group.visible = next === 2 || (next === 0 ? i < 2 : i > 0);
    });
    if (this.ambient) (this.ambient.material as THREE.PointsMaterial).opacity = [0.34, 0.46, 0.58][next];
  }

  update(frameDt: number, camX = 0, camZ = 0): void {
    this.time += frameDt;
    const t = this.time;
    if (this.ground) {
      // snap to the texture period so the pattern stays fixed to the world
      this.ground.position.x = Math.round(camX / this.tile) * this.tile;
      this.ground.position.z = Math.round(camZ / this.tile) * this.tile;
    }
    for (const landmark of this.landmarks) {
      landmark.group.position.x = camX + wrapDelta(landmark.x - camX, this.half);
      landmark.group.position.z = camZ + wrapDelta(landmark.z - camZ, this.half);
      landmark.group.scale.setScalar(this.presence);
    }
    this.updateAmbient(t, camX, camZ);
    // props: always re-place relative to the camera so they wrap with the world
    for (const pm of this.propMeshes) {
      if (this.propAnims.some((pa) => pa.mesh === pm)) continue;
      const base = this.propBase.get(pm)!;
      const n = pm.count;
      for (let i = 0; i < n; i++) {
        const x = camX + wrapDelta(base[i * 4] - camX, this.half);
        const z = camZ + wrapDelta(base[i * 4 + 2] - camZ, this.half);
        const sc = base[i * 4 + 3] * this.presence;
        _p.set(x, base[i * 4 + 1] * this.presence, z);
        _q.setFromAxisAngle(_up, i * 1.7);
        _s.set(sc, sc * this.propScaleY.get(pm)!, sc);
        _m.compose(_p, _q, _s);
        pm.setMatrixAt(i, _m);
      }
      pm.instanceMatrix.needsUpdate = true;
    }
    for (const pa of this.propAnims) {
      const n = pa.mesh.count;
      for (let i = 0; i < n; i++) {
        const x = camX + wrapDelta(pa.base[i * 4] - camX, this.half);
        const y = pa.base[i * 4 + 1];
        const z = camZ + wrapDelta(pa.base[i * 4 + 2] - camZ, this.half);
        const s = pa.base[i * 4 + 3] * this.presence;
        let sy = s;
        let sxz = s;
        let dy = 0;
        if (pa.anim === 'bob') dy = Math.sin(t * 1.5 + i) * 0.3;
        else if (pa.anim === 'pulse') {
          const f = 1 + Math.sin(t * 2 + i) * 0.05;
          sxz = s * f;
          sy = s * (2 - f);
        } else if (pa.anim === 'wobble') sxz = s * (1 + Math.sin(t * 2.5 + i) * 0.05);
        _p.set(x, y + dy, z);
        _q.setFromAxisAngle(_up, pa.anim === 'spin' ? t + i : i * 1.7);
        _s.set(sxz, sy, sxz);
        _m.compose(_p, _q, _s);
        pa.mesh.setMatrixAt(i, _m);
      }
      pa.mesh.instanceMatrix.needsUpdate = true;
    }
  }

  clear(): void {
    for (const m of this.propMeshes) {
      this.group.remove(m);
      (m.material as THREE.Material).dispose();
    }
    for (const o of this.propOutlines) {
      this.group.remove(o);
      (o.material as THREE.Material).dispose();
    }
    this.propOutlines = [];
    this.propMeshes = [];
    this.propAnims = [];
    this.propBase.clear();
    this.propScaleY.clear();
    for (const landmark of this.landmarks) {
      this.group.remove(landmark.group);
      landmark.group.traverse((o) => {
        const mesh = o as THREE.Mesh;
        mesh.geometry?.dispose();
        const mat = mesh.material as THREE.Material | THREE.Material[] | undefined;
        if (Array.isArray(mat)) mat.forEach((m) => m.dispose());
        else mat?.dispose();
      });
    }
    this.landmarks = [];
    this.stageIndex = -1;
    if (this.ambient) {
      this.group.remove(this.ambient);
      this.ambient.geometry.dispose();
      (this.ambient.material as THREE.Material).dispose();
      this.ambient = null;
      this.ambientBase = null;
      this.ambientProfile = null;
    }
    if (this.ground) {
      this.group.remove(this.ground);
      (this.ground.material as THREE.MeshStandardMaterial).map?.dispose();
      (this.ground.material as THREE.Material).dispose();
      this.ground.geometry.dispose();
      this.ground = null;
    }
    if (this.ring) {
      this.group.remove(this.ring);
      this.ring.geometry.dispose();
      this.ring = null;
    }
  }

  private buildLandmarks(era: EraDef, rng: Rng): void {
    const profile = environmentProfile(era.index);
    const positions = [[-18, -14], [20, -10], [-22, 18], [19, 22]] as const;
    for (let i = 0; i < positions.length; i++) {
      const landmark = makeLandmark(profile.landmark, era.palette.groundAlt, era.palette.accent);
      const jitter = i * 0.37;
      const x = positions[i][0] + rng.range(-3, 3);
      const z = positions[i][1] + rng.range(-3, 3);
      landmark.rotation.y = jitter + rng.range(-0.35, 0.35);
      landmark.scale.setScalar(rng.range(0.85, 1.2));
      landmark.position.set(x, 0, z);
      this.group.add(landmark);
      this.landmarks.push({ group: landmark, x, z });
    }
  }

  private buildAmbient(era: EraDef, rng: Rng): void {
    const profile = environmentProfile(era.index);
    const pos = new Float32Array(profile.ambientCount * 3);
    for (let i = 0; i < profile.ambientCount; i++) {
      pos[i * 3] = rng.range(-42, 42);
      pos[i * 3 + 1] = rng.range(0.4, 15);
      pos[i * 3 + 2] = rng.range(-42, 42);
    }
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
    const size = profile.ambient === 'data' || profile.ambient === 'shards' ? 0.13 : 0.1;
    const mat = new THREE.PointsMaterial({ color: profile.ambientColor, size, transparent: true, opacity: 0.48, depthWrite: false, blending: profile.ambient === 'snow' || profile.ambient === 'dust' ? THREE.NormalBlending : THREE.AdditiveBlending });
    this.ambient = new THREE.Points(geo, mat);
    this.ambient.frustumCulled = false;
    this.ambient.visible = this.detailOn;
    this.ambientBase = pos.slice();
    this.ambientProfile = profile;
    this.group.add(this.ambient);
  }

  private updateAmbient(t: number, camX: number, camZ: number): void {
    if (!this.ambient || !this.ambientBase || !this.ambientProfile) return;
    this.ambient.position.set(camX, 0, camZ);
    const attr = this.ambient.geometry.getAttribute('position') as THREE.BufferAttribute;
    const down = ['snow', 'ash', 'data', 'shards'].includes(this.ambientProfile.ambient);
    const rise = ['bubbles', 'plankton', 'embers', 'steam'].includes(this.ambientProfile.ambient);
    for (let i = 0; i < attr.count; i++) {
      const x0 = this.ambientBase[i * 3];
      const y0 = this.ambientBase[i * 3 + 1];
      const z0 = this.ambientBase[i * 3 + 2];
      const speed = 0.35 + (i % 9) * 0.055;
      const y = down ? 15 - ((15 - y0 + t * speed * 2.5) % 15) : rise ? 0.4 + ((y0 + t * speed) % 14.6) : y0 + Math.sin(t * speed + i) * 0.35;
      attr.setXYZ(i, x0 + Math.sin(t * 0.18 + i) * (down ? 1.4 : 0.35), y, z0 + Math.cos(t * 0.14 + i * 0.7) * 0.25);
    }
    attr.needsUpdate = true;
  }
}

function makeLandmark(kind: LandmarkKind, baseColor: number, accentColor: number): THREE.Group {
  const group = new THREE.Group();
  const base = toonMaterial(baseColor);
  const accent = toonMaterial(accentColor, accentColor, 0.65);
  const add = (geo: THREE.BufferGeometry, x: number, y: number, z: number, mat = base, ry = 0) => {
    const mesh = new THREE.Mesh(geo, mat.clone());
    mesh.position.set(x, y, z);
    mesh.rotation.y = ry;
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    group.add(mesh);
  };
  switch (kind) {
    case 'vent': add(new THREE.CylinderGeometry(.9, 1.4, 1.1, 9), 0, .55, 0); add(new THREE.SphereGeometry(.35, 10, 8), 0, 1.35, 0, accent); break;
    case 'reef': for (const [x, h] of [[-.7, 2.5], [0, 3.4], [.75, 2.1]] as const) add(new THREE.CylinderGeometry(.12, .4, h, 7), x, h / 2, 0, x === 0 ? accent : base, x); break;
    case 'fossils': for (let i = -2; i <= 2; i++) add(new THREE.TorusGeometry(1.3, .12, 6, 12, Math.PI), i * .52, .75 + Math.abs(i) * .12, 0, base, Math.PI / 2); break;
    case 'iceArch': add(new THREE.ConeGeometry(.55, 3.8, 6), -1.1, 1.9, 0); add(new THREE.ConeGeometry(.55, 3.8, 6), 1.1, 1.9, 0); add(new THREE.BoxGeometry(2.3, .35, .5), 0, 3.45, 0, accent); break;
    case 'stones': for (let i = 0; i < 5; i++) { const a = i / 5 * Math.PI * 2; add(new THREE.BoxGeometry(.55, 2.4, .65), Math.cos(a) * 1.5, 1.2, Math.sin(a) * 1.5, i === 0 ? accent : base, -a); } break;
    case 'ruins': add(new THREE.BoxGeometry(.55, 3.2, .55), -1.1, 1.6, 0); add(new THREE.BoxGeometry(.55, 2.3, .55), 1.1, 1.15, 0); add(new THREE.BoxGeometry(2.7, .4, .65), 0, 2.65, 0, accent); break;
    case 'gate': add(new THREE.BoxGeometry(.8, 3.2, .8), -1.3, 1.6, 0); add(new THREE.BoxGeometry(.8, 3.2, .8), 1.3, 1.6, 0); add(new THREE.BoxGeometry(3.3, .7, .9), 0, 3.2, 0, accent); break;
    case 'boiler': add(new THREE.CylinderGeometry(1.1, 1.1, 2.4, 10), 0, 1.2, 0); add(new THREE.CylinderGeometry(.28, .38, 3.9, 8), -.65, 2.25, 0, accent); add(new THREE.TorusGeometry(.65, .13, 7, 16), 0, 1.2, .9, accent); break;
    case 'holo': add(new THREE.BoxGeometry(.35, 3.8, .35), -.85, 1.9, 0, accent); add(new THREE.BoxGeometry(.35, 5.1, .35), .15, 2.55, 0); add(new THREE.BoxGeometry(.35, 2.9, .35), 1.05, 1.45, 0, accent); break;
    case 'hourglass': add(new THREE.ConeGeometry(1.1, 1.8, 8), 0, 2.65, 0, accent); const lower = new THREE.Mesh(new THREE.ConeGeometry(1.1, 1.8, 8), accent.clone()); lower.position.y = .85; lower.rotation.z = Math.PI; group.add(lower); add(new THREE.TorusGeometry(1.15, .12, 7, 18), 0, 3.6, 0); add(new THREE.TorusGeometry(1.15, .12, 7, 18), 0, 0, 0); break;
  }
  base.dispose();
  accent.dispose();
  return group;
}

function hex(c: number): string {
  return '#' + c.toString(16).padStart(6, '0');
}

/** Draws a tileable 256×256 ground texture for the era's groundStyle. */
function drawGround(era: EraDef): HTMLCanvasElement {
  const size = 256;
  const c = document.createElement('canvas');
  c.width = c.height = size;
  const ctx = c.getContext('2d')!;
  const p = era.palette;
  const rng = new Rng('ground:' + era.id);
  ctx.fillStyle = hex(p.ground);
  ctx.fillRect(0, 0, size, size);
  ctx.fillStyle = hex(p.groundAlt);
  ctx.strokeStyle = hex(p.groundAlt);
  switch (era.environment.groundStyle) {
    case 'cells':
      for (let i = 0; i < 40; i++) {
        ctx.globalAlpha = rng.range(0.15, 0.5);
        ctx.beginPath();
        ctx.ellipse(rng.range(0, size), rng.range(0, size), rng.range(8, 30), rng.range(8, 30), rng.range(0, 3), 0, Math.PI * 2);
        ctx.fill();
      }
      break;
    case 'ripples':
      ctx.lineWidth = 2;
      for (let i = 0; i < 18; i++) {
        ctx.globalAlpha = rng.range(0.1, 0.35);
        ctx.beginPath();
        ctx.arc(rng.range(0, size), rng.range(0, size), rng.range(10, 60), 0, Math.PI * 2);
        ctx.stroke();
      }
      break;
    case 'cracked':
      ctx.lineWidth = 3;
      ctx.globalAlpha = 0.5;
      for (let i = 0; i < 25; i++) {
        ctx.beginPath();
        let x = rng.range(0, size);
        let y = rng.range(0, size);
        ctx.moveTo(x, y);
        for (let k = 0; k < 5; k++) {
          x += rng.range(-30, 30);
          y += rng.range(-30, 30);
          ctx.lineTo(x, y);
        }
        ctx.stroke();
      }
      break;
    case 'snow':
      for (let i = 0; i < 400; i++) {
        ctx.globalAlpha = rng.range(0.2, 0.7);
        ctx.fillRect(rng.range(0, size), rng.range(0, size), 2, 2);
      }
      break;
    case 'tiles':
      ctx.globalAlpha = 0.6;
      ctx.lineWidth = 2;
      for (let i = 0; i <= 4; i++) {
        ctx.beginPath();
        ctx.moveTo((i * size) / 4, 0);
        ctx.lineTo((i * size) / 4, size);
        ctx.moveTo(0, (i * size) / 4);
        ctx.lineTo(size, (i * size) / 4);
        ctx.stroke();
      }
      break;
    case 'grid':
      ctx.globalAlpha = 0.8;
      ctx.lineWidth = 1;
      for (let i = 0; i <= 8; i++) {
        ctx.beginPath();
        ctx.moveTo((i * size) / 8, 0);
        ctx.lineTo((i * size) / 8, size);
        ctx.moveTo(0, (i * size) / 8);
        ctx.lineTo(size, (i * size) / 8);
        ctx.stroke();
      }
      break;
    case 'void':
      for (let i = 0; i < 120; i++) {
        ctx.globalAlpha = rng.range(0.3, 1);
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(rng.range(0, size), rng.range(0, size), 1, 1);
      }
      break;
  }
  drawEraMotif(ctx, environmentProfile(era.index), p.accent, size, rng);
  ctx.globalAlpha = 1;
  return c;
}

function drawEraMotif(ctx: CanvasRenderingContext2D, profile: EnvironmentProfile, accent: number, size: number, rng: Rng): void {
  ctx.strokeStyle = hex(accent);
  ctx.fillStyle = hex(accent);
  ctx.lineWidth = profile.motif === 'lanes' || profile.motif === 'rails' ? 3 : 1.5;
  ctx.globalAlpha = profile.motif === 'lanes' ? 0.18 : 0.1;
  if (profile.motif === 'rails' || profile.motif === 'lanes' || profile.motif === 'mosaic' || profile.motif === 'cobbles') {
    const step = profile.motif === 'cobbles' ? 32 : 48;
    for (let i = -size; i < size * 2; i += step) {
      ctx.beginPath(); ctx.moveTo(i, 0); ctx.lineTo(i + (profile.motif === 'mosaic' ? size : 0), size); ctx.stroke();
      if (profile.motif === 'rails') { ctx.beginPath(); ctx.moveTo(0, i); ctx.lineTo(size, i); ctx.stroke(); }
    }
  } else {
    for (let i = 0; i < 18; i++) {
      const x = rng.range(0, size); const y = rng.range(0, size);
      ctx.beginPath();
      if (profile.motif === 'tracks') { ctx.ellipse(x, y, 4, 10, rng.range(0, Math.PI), 0, Math.PI * 2); ctx.fill(); }
      else { ctx.moveTo(x, y); for (let k = 0; k < 4; k++) ctx.lineTo(x + rng.range(-35, 35), y + rng.range(-35, 35)); ctx.stroke(); }
    }
  }
}
